/**
 * Camada de tokens — secção 2 do guia de clonagem "Eleva".
 *
 * Regra que não se viola: dois matizes apenas. Toda a variação vem de
 * luminosidade e opacidade.
 *
 * O tema escuro (`darkTokens`) é o original — não alterar valores sem medir
 * contrastes em `src/__tests__/contrast.test.ts`.
 *
 * O tema claro (`lightTokens`) espelha a mesma estrutura semântica.
 */

export type McTheme = 'dark' | 'light';

/** Matiz A escuro + matiz B verde — tema predefinido da marca. */
export const darkTokens = {
  bg: '#050F1C',
  bg2: '#0B1E33',
  bg3: '#12293F',
  ink: '#E8F6EE',
  ink2: '#A9C4B8',
  ink3: '#7E9A8F',
  accent: '#2FE07A',
  accent2: '#7DF5AE',
  depth: '#1B75BC',
  deep: '#04150C',
  onAccent: '#04150C',
  card: 'rgba(6, 20, 14, 0.62)',
} as const;

/** Tema claro — branco puxado ao verde, acento ligeiramente mais profundo. */
export const lightTokens = {
  bg: '#F4F9F7',
  bg2: '#FFFFFF',
  bg3: '#E8F2ED',
  ink: '#0A1F16',
  ink2: '#3D5C4F',
  ink3: '#5A7268',
  accent: '#097038',
  accent2: '#2FE07A',
  depth: '#1B75BC',
  deep: '#04150C',
  onAccent: '#F4F9F7',
  card: 'rgba(255, 255, 255, 0.88)',
} as const;

/** @deprecated Use `darkTokens` — alias para compatibilidade. */
export const tokens = darkTokens;

export type ThemeTokenSet = typeof darkTokens | typeof lightTokens;

export const darkRgb = {
  ink: [232, 246, 238],
  accent: [47, 224, 122],
  depth: [27, 117, 188],
} as const;

export const lightRgb = {
  ink: [10, 31, 22],
  accent: [9, 112, 56],
  depth: [27, 117, 188],
} as const;

/** Opacidades das bordas — derivadas de `ink`, nunca greys inventados. */
export const lineAlpha = { subtle: 0.1, strong: 0.18 } as const;
export const lightLineAlpha = { subtle: 0.1, strong: 0.16 } as const;

/** Alfa único que governa todo o brilho do site. */
export const glowAlpha = 0.35;
export const lightGlowAlpha = 0.22;

export const rgb = darkRgb;

export const line = `rgba(${darkRgb.ink.join(', ')}, ${lineAlpha.subtle})`;
export const line2 = `rgba(${darkRgb.ink.join(', ')}, ${lineAlpha.strong})`;
export const glow = `rgba(${darkRgb.accent.join(', ')}, ${glowAlpha})`;

export const lightLine = `rgba(${lightRgb.ink.join(', ')}, ${lightLineAlpha.subtle})`;
export const lightLine2 = `rgba(${lightRgb.ink.join(', ')}, ${lightLineAlpha.strong})`;
export const lightGlow = `rgba(${lightRgb.accent.join(', ')}, ${lightGlowAlpha})`;

/** Cor semântica de erro — igual em ambos os temas. */
export const danger = '#E5484D';
export const warning = '#E0A64C';

/** Superfícies semânticas — mapeiam rgba hardcoded nos componentes. */
export interface SurfaceTokens {
  nav: string;
  navSolid: string;
  hover: string;
  ghost: string;
  ghostStrong: string;
  field: string;
  band: string;
  card: string;
  cardActive: string;
  tableHead: string;
  track: string;
  seal: string;
  backdropEdge: string;
  veilMid: string;
  chartGrid: string;
  chartCursor: string;
  tooltipBg: string;
  tooltipBorder: string;
}

export const darkSurfaces: SurfaceTokens = {
  nav: 'rgba(5, 15, 28, 0.82)',
  navSolid: 'rgba(5, 15, 28, 0.97)',
  hover: 'rgba(232, 246, 238, 0.06)',
  ghost: 'rgba(232, 246, 238, 0.04)',
  ghostStrong: 'rgba(232, 246, 238, 0.08)',
  field: 'rgba(0, 0, 0, 0.25)',
  band: 'rgba(11, 30, 51, 0.4)',
  card: 'rgba(6, 20, 14, 0.62)',
  cardActive: 'rgba(0, 0, 0, 0.30)',
  tableHead: 'rgba(11, 30, 51, 0.5)',
  track: 'rgba(232, 246, 238, 0.08)',
  seal: 'radial-gradient(circle, rgba(47,224,122,0.28), rgba(47,224,122,0.04) 70%)',
  backdropEdge: '#030a14',
  veilMid: 'rgba(5, 15, 28, 0.55)',
  chartGrid: 'rgba(232, 246, 238, 0.08)',
  chartCursor: 'rgba(232, 246, 238, 0.04)',
  tooltipBg: 'rgba(11, 30, 51, 0.95)',
  tooltipBorder: 'rgba(232, 246, 238, 0.18)',
};

export const lightSurfaces: SurfaceTokens = {
  nav: 'rgba(244, 249, 247, 0.88)',
  navSolid: 'rgba(255, 255, 255, 0.98)',
  hover: 'rgba(10, 31, 22, 0.05)',
  ghost: 'rgba(10, 31, 22, 0.03)',
  ghostStrong: 'rgba(10, 31, 22, 0.06)',
  field: 'rgba(255, 255, 255, 0.95)',
  band: 'rgba(232, 242, 237, 0.75)',
  card: 'rgba(255, 255, 255, 0.92)',
  cardActive: 'rgba(21, 154, 82, 0.06)',
  tableHead: 'rgba(232, 242, 237, 0.9)',
  track: 'rgba(10, 31, 22, 0.08)',
  seal: 'radial-gradient(circle, rgba(9,112,56,0.18), rgba(9,112,56,0.04) 70%)',
  backdropEdge: '#E2EDE8',
  veilMid: 'rgba(244, 249, 247, 0.65)',
  chartGrid: 'rgba(10, 31, 22, 0.08)',
  chartCursor: 'rgba(10, 31, 22, 0.04)',
  tooltipBg: 'rgba(255, 255, 255, 0.98)',
  tooltipBorder: 'rgba(10, 31, 22, 0.12)',
};

export const themeMeta: Record<McTheme, { themeColor: string; colorScheme: 'dark' | 'light' }> = {
  dark: { themeColor: '#050F1C', colorScheme: 'dark' },
  light: { themeColor: '#F4F9F7', colorScheme: 'light' },
};

function surfaceVars(s: SurfaceTokens): [string, string][] {
  return [
    ['--mc-surface-nav', s.nav],
    ['--mc-surface-nav-solid', s.navSolid],
    ['--mc-surface-hover', s.hover],
    ['--mc-surface-ghost', s.ghost],
    ['--mc-surface-ghost-strong', s.ghostStrong],
    ['--mc-surface-field', s.field],
    ['--mc-surface-band', s.band],
    ['--mc-surface-card', s.card],
    ['--mc-surface-card-active', s.cardActive],
    ['--mc-surface-table-head', s.tableHead],
    ['--mc-surface-track', s.track],
    ['--mc-surface-seal', s.seal],
    ['--mc-backdrop-edge', s.backdropEdge],
    ['--mc-veil-mid', s.veilMid],
    ['--mc-chart-grid', s.chartGrid],
    ['--mc-chart-cursor', s.chartCursor],
    ['--mc-tooltip-bg', s.tooltipBg],
    ['--mc-tooltip-border', s.tooltipBorder],
  ];
}

function tokenVars(
  t: ThemeTokenSet,
  l: string,
  l2: string,
  g: string,
  s: SurfaceTokens,
): [string, string][] {
  return [
    ['--mc-bg', t.bg],
    ['--mc-bg-2', t.bg2],
    ['--mc-bg-3', t.bg3],
    ['--mc-ink', t.ink],
    ['--mc-ink-2', t.ink2],
    ['--mc-ink-3', t.ink3],
    ['--mc-line', l],
    ['--mc-line-2', l2],
    ['--mc-accent', t.accent],
    ['--mc-accent-2', t.accent2],
    ['--mc-glow', g],
    ['--mc-depth', t.depth],
    ['--mc-deep', t.deep],
    ['--mc-on-accent', t.onAccent],
    ['--mc-card', t.card],
    ['--mc-danger', danger],
    ['--mc-warning', warning],
    ...surfaceVars(s),
  ];
}

/** Bloco CSS `:root` / `[data-theme]` gerado a partir das constantes. */
export function cssVariables(theme: McTheme = 'light'): string {
  const entries =
    theme === 'light'
      ? tokenVars(lightTokens, lightLine, lightLine2, lightGlow, lightSurfaces)
      : tokenVars(darkTokens, line, line2, glow, darkSurfaces);
  return entries.map(([k, v]) => `  ${k}: ${v};`).join('\n');
}

/** Escala tipográfica fluida — um clamp() substitui três media queries. */
export const fluidType = {
  h1: 'clamp(2.5rem, 5vw, 4.5rem)',
  h2: 'clamp(2rem, 4.5vw, 3.25rem)',
  h3: 'clamp(1.375rem, 3vw, 2rem)',
  cardTitle: 'clamp(1.125rem, 2.2vw, 1.5rem)',
  lead: 'clamp(1rem, 1.6vw, 1.25rem)',
  body: 'clamp(0.9375rem, 1.1vw, 1.0625rem)',
  label: 'clamp(0.625rem, 0.9vw, 0.6875rem)',
} as const;

export const radii = {
  pill: '999px',
  card: '18px',
  cardLg: '24px',
  field: '12px',
} as const;

export const layout = {
  content: '1240px',
  reading: '980px',
} as const;

export const easeOut = 'cubic-bezier(.22,.61,.36,1)';

/** Lê uma variável CSS do documento (para gráficos Recharts). */
export function readCssVar(name: string, fallback = ''): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}
